// @ts-strict-ignore
import { Component, ChangeDetectionStrategy } from "@angular/core";
import { EvcsComponent } from "src/app/shared/components/edge/config-components/evcs/evcsComponent";
import { AbstractFlatWidget } from "src/app/shared/components/flat/abstract-flat-widget";
import { ChannelAddress, CurrentData, EdgeConfig } from "../../../../../../shared/shared";

@Component({
    selector: "consumptionWidget",
    templateUrl: "./flat.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class FlatComponent extends AbstractFlatWidget {
    protected evcsComponents: EvcsComponent[] = [];
    protected heatComponents: EdgeConfig.Component[] = [];
    protected consumptionMeterComponents: EdgeConfig.Component[] = [];
    protected totalOtherEnergy: number | null;
    /**
     * Ob diese Edge einen echten Netz-Zähler hat. Ohne Zähler liefert der
     * Sum-Baustein einen irreführenden Default fuer _sum/ConsumptionActiveEnergy
     * (=Erzeugung, siehe [[project_excello_teuschnitz_pv_monitoring]]). Betrifft
     * NUR die Gesamtsumme - einzeln gemessene EVCS/Heat/Verbrauchszähler
     * bleiben unverändert, die sind unabhängig vom Netz-Zähler echt gemessen.
     * Im Template zusätzlich verwendet, da die "keine Untergeräte"-Zeile
     * direkt per channelAddress bindet, nicht über eine TS-Property.
     */
    protected hasGridMeter: boolean = true;

    protected override afterIsInitialized(): void {
        this.hasGridMeter = this.config
            ?.getComponentsImplementingNature("io.openems.edge.meter.api.ElectricityMeter")
            .some(c => c.isEnabled && this.config.isTypeGrid(c)) ?? true;
    }

    protected override getChannelAddresses(): ChannelAddress[] {
        const channels: ChannelAddress[] = [new ChannelAddress("_sum", "ConsumptionActiveEnergy")];

        this.evcsComponents = EvcsComponent.getComponents(this.config, this.edge);

        this.heatComponents = this.config
            ?.getComponentsImplementingNature("io.openems.edge.heat.api.Heat")
            .filter(
                (component) =>
                    !(component.factoryId === "Controller.Heat.Heatingelement") && !component.isEnabled === false,
            );
        channels.push(
            ...this.heatComponents.map((component) => new ChannelAddress(component.id, "ActiveProductionEnergy")),
        );

        this.consumptionMeterComponents = this.config
            ?.getComponentsImplementingNature("io.openems.edge.meter.api.ElectricityMeter")
            .filter((component) => {
                const natureIds = this.config?.getNatureIdsByFactoryId(component.factoryId);
                const isEvcs = natureIds.includes("io.openems.edge.evcs.api.Evcs");
                const isHeat = natureIds.includes("io.openems.edge.heat.api.Heat");

                return (
                    component.isEnabled &&
                    this.config?.isTypeConsumptionMetered(component) &&
                    isEvcs === false &&
                    isHeat === false
                );
            });

        return channels;
    }

    protected override onCurrentData(currentData: CurrentData): void {
        this.totalOtherEnergy = this.getTotalOtherEnergy(currentData);
    }

    /**
     * Gets the totalOtherEnergy
     *
     * @param currentData The current data
     * @returns The total other Energy, or null if there is no real grid meter
     *     (the _sum/ConsumptionActiveEnergy this is based on would otherwise
     *     just mirror production, see {@link hasGridMeter})
     */
    private getTotalOtherEnergy(currentData: CurrentData): number | null {
        if (!this.hasGridMeter) {
            return null;
        }

        let otherEnergy: number = 0;

        this.evcsComponents.forEach((evcs) => {
            otherEnergy += currentData.allComponents[evcs.energyChannel.toString()] ?? 0;
        });

        [...this.consumptionMeterComponents, ...this.heatComponents].forEach((component) => {
            otherEnergy += currentData.allComponents[component.id + "/ActiveProductionEnergy"] ?? 0;
        });

        return currentData.allComponents["_sum/ConsumptionActiveEnergy"] - otherEnergy;
    }
}
